import React from 'react';
import { Star, Phone, Calendar } from 'lucide-react';
import { CustomerAnalytics } from '@/types/analytics';

interface TopCustomerCardProps {
  customers: CustomerAnalytics[];
  loading?: boolean;
  error?: string;
  className?: string;
}

const TopCustomerCard: React.FC<TopCustomerCardProps> = ({
  customers,
  loading = false,
  error,
  className = ''
}) => {
  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatPhone = (phone?: string): string => {
    if (!phone) return 'N/A';
    // Format phone number for display
    return phone.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3');
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-32 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center space-x-3">
                <div className="h-8 w-8 bg-gray-200 rounded-full"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                </div>
                <div className="h-4 bg-gray-200 rounded w-12"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Customers</h3>
        <div className="flex items-center justify-center h-32 text-gray-500">
          <div className="text-center">
            <p className="text-sm">Unable to load customer data</p>
            <p className="text-xs text-gray-400 mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!customers || customers.length === 0) {
    return (
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Customers</h3>
        <div className="flex items-center justify-center h-32 text-gray-500">
          <div className="text-center">
            <p className="text-sm">No customer data available</p>
            <p className="text-xs text-gray-400 mt-1">Complete some orders to see top customers</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center space-x-2 mb-4">
        <Star className="h-5 w-5 text-yellow-500" />
        <h3 className="text-lg font-semibold text-gray-900">Top Customers</h3>
      </div>
      
      <div className="space-y-4">
        {customers.slice(0, 5).map((customer, index) => (
          <div key={customer.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-8 w-8 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">
                    {customer.name.charAt(0).toUpperCase()}
                  </span>
                </div>
              </div>
              
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {customer.name}
                </p>
                <div className="flex items-center space-x-2 text-xs text-gray-500">
                  <div className="flex items-center space-x-1">
                    <Phone className="h-3 w-3" />
                    <span>{formatPhone(customer.phone_number)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(customer.last_order_date)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-sm font-semibold text-gray-900">
                ₹{customer.total_sales.toLocaleString()}
              </p>
              <p className="text-xs text-gray-500">
                {customer.order_count} orders
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TopCustomerCard;
